
import React, { useRef } from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import TableActionMenu from "./TableActionMenu";
import { cn } from "@/lib/utils";

interface SortableTableRowProps<T> {
  item: T;
  columns: {
    key: keyof T | string;
    render?: (item: T) => React.ReactNode;
    className?: string;
  }[];
  selected?: boolean;
  onSelect?: (checked: boolean, event?: React.MouseEvent) => void;
  onPreSelect?: (shiftKey: boolean) => void;
  onClick?: (event: React.MouseEvent) => void;
  keyExtractor: (item: T) => string;
}

function SortableTableRow<T>({
  item,
  columns,
  selected,
  onSelect,
  onPreSelect,
  onClick,
  keyExtractor
}: SortableTableRowProps<T>) {
  // Track whether shift was held during the initial pointer down
  const shiftPressedRef = useRef<boolean>(false);

  const handleRowClick = (event: React.MouseEvent) => {
    // Don't call onSelect directly here anymore - just call onClick
    // This prevents double-counting when the row is clicked
    // The parent component will handle the selection logic
    if (onClick && !event.defaultPrevented) {
      onClick(event);
    }
  };

  const handleCheckboxChange = (checked: boolean, event?: any) => {
    if (onSelect) {
      onSelect(checked, event);
    }
  };

  // Capture mouse event early so shiftKey is available before onCheckedChange fires
  const handleCheckboxMouseDown = (event: React.MouseEvent) => {
    event.stopPropagation();
    const isShift = !!event.shiftKey;
    // Preserve any previously detected shift press until selection resolves
    if (isShift) {
      shiftPressedRef.current = true;
      if (onPreSelect) onPreSelect(true);
    }
  };

  // Handler for the checkbox cell click
  const handleCheckboxCellClick = (event: React.MouseEvent) => {
    // Stop propagation to prevent the row click from triggering
    event.stopPropagation();
    // Note: Do not manually toggle selection here; the Checkbox onCheckedChange will handle it.
  };

  // Capture mouse down early on the cell to reliably read shiftKey
  const handleCheckboxCellMouseDownCapture = (event: React.MouseEvent) => {
    event.stopPropagation();
    const isShift = !!event.shiftKey;
    if (isShift) {
      shiftPressedRef.current = true;
      if (onPreSelect) onPreSelect(true);
    }
  };

  return (
    <TableRow
      className={cn({ 
        "cursor-pointer": !!(onClick || onSelect),
        "bg-muted/20": selected
      })}
      onClick={handleRowClick}
    >
      {onSelect && (
        <TableCell 
          className="p-2"
          onClick={handleCheckboxCellClick} // Add click handler to the cell
          onMouseDownCapture={handleCheckboxCellMouseDownCapture}
        >
          <Checkbox
            checked={selected}
            onCheckedChange={(checked) => {
              // Build minimal event-like object carrying shiftKey information
              const evt = { shiftKey: shiftPressedRef.current } as unknown as React.MouseEvent;
              handleCheckboxChange(checked as boolean, evt);
              // Reset shift state after a brief delay to ensure the event is processed
              setTimeout(() => {
                shiftPressedRef.current = false;
              }, 0);
            }}
            onMouseDown={handleCheckboxMouseDown}
          />
        </TableCell>
      )}
      {columns.map(column => (
        <TableCell 
          key={`${keyExtractor(item)}-${String(column.key)}`}
          className={cn("p-4", column.className)}
        >
          {column.key === "actions" && column.render ? (
            <div onClick={(e) => e.stopPropagation()}>
              <TableActionMenu item={item} render={column.render} />
            </div>
          ) : column.render ? (
            column.render(item)
          ) : (
            item[column.key as keyof T]?.toString() || ''
          )}
        </TableCell>
      ))}
    </TableRow>
  );
}

export default SortableTableRow;
